/**
 * @fileoverview ClubOS Tactics & Formation page — pitch visualizer with formation selector
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Mock data
import { squad } from "data/mockData";

// TODO: Replace with API call when backend is ready
// import { coachingService } from "api";

const FORMATIONS = ["4-3-3", "4-2-3-1", "3-5-2", "4-4-2", "5-3-2"];

/** Formation layouts: positions as [x%, y%] on pitch */
const FORMATION_POSITIONS = {
  "4-3-3": [
    { label: "GK",  x: 50, y: 90 },
    { label: "RB",  x: 80, y: 72 }, { label: "RCB", x: 62, y: 72 }, { label: "LCB", x: 38, y: 72 }, { label: "LB",  x: 20, y: 72 },
    { label: "RCM", x: 70, y: 52 }, { label: "CM",  x: 50, y: 48 }, { label: "LCM", x: 30, y: 52 },
    { label: "RW",  x: 78, y: 28 }, { label: "ST",  x: 50, y: 20 }, { label: "LW",  x: 22, y: 28 },
  ],
  "4-2-3-1": [
    { label: "GK",  x: 50, y: 90 },
    { label: "RB",  x: 80, y: 72 }, { label: "RCB", x: 62, y: 72 }, { label: "LCB", x: 38, y: 72 }, { label: "LB",  x: 20, y: 72 },
    { label: "RDM", x: 62, y: 58 }, { label: "LDM", x: 38, y: 58 },
    { label: "RAM", x: 75, y: 38 }, { label: "CAM", x: 50, y: 35 }, { label: "LAM", x: 25, y: 38 },
    { label: "ST",  x: 50, y: 18 },
  ],
  "3-5-2": [
    { label: "GK",  x: 50, y: 90 },
    { label: "RCB", x: 72, y: 72 }, { label: "CB",  x: 50, y: 72 }, { label: "LCB", x: 28, y: 72 },
    { label: "RWB", x: 85, y: 52 }, { label: "RCM", x: 68, y: 50 }, { label: "CM",  x: 50, y: 46 }, { label: "LCM", x: 32, y: 50 }, { label: "LWB", x: 15, y: 52 },
    { label: "RST", x: 62, y: 20 }, { label: "LST", x: 38, y: 20 },
  ],
  "4-4-2": [
    { label: "GK",  x: 50, y: 90 },
    { label: "RB",  x: 80, y: 72 }, { label: "RCB", x: 62, y: 72 }, { label: "LCB", x: 38, y: 72 }, { label: "LB",  x: 20, y: 72 },
    { label: "RM",  x: 80, y: 50 }, { label: "RCM", x: 60, y: 50 }, { label: "LCM", x: 40, y: 50 }, { label: "LM",  x: 20, y: 50 },
    { label: "RST", x: 62, y: 20 }, { label: "LST", x: 38, y: 20 },
  ],
  "5-3-2": [
    { label: "GK",  x: 50, y: 90 },
    { label: "RWB", x: 85, y: 65 }, { label: "RCB", x: 68, y: 72 }, { label: "CB",  x: 50, y: 75 }, { label: "LCB", x: 32, y: 72 }, { label: "LWB", x: 15, y: 65 },
    { label: "RCM", x: 65, y: 48 }, { label: "CM",  x: 50, y: 44 }, { label: "LCM", x: 35, y: 48 },
    { label: "RST", x: 62, y: 20 }, { label: "LST", x: 38, y: 20 },
  ],
};

/** Assign players to formation positions */
const LINEUP_PLAYERS = [
  "J. Hartley", "T. Okonkwo", "Y. Müller", "O. Adeyemi", "B. Ashford",
  "K. Pearson", "F. Rennetti", "A. Lefebvre",
  "J. Osei", "S. Holloway", "M. Tetteh",
];

const SUBS = ["M. de Bruijn", "C. Walsh", "A. Popov", "E. Kaya", "O. Svensson", "P. Ruiz", "D. Okafor"];

function Tactics() {
  const [activeFormation, setActiveFormation] = useState("4-3-3");
  const [notes, setNotes] = useState("High press from the front, quick transitions through midfield. Target opposition full-backs with wide runners.");

  // TODO: Uncomment when API is ready
  // const [tactics, setTactics] = useState(null);
  // useEffect(() => { coachingService.getTactics().then(setTactics).catch(console.error); }, []);

  const positions = FORMATION_POSITIONS[activeFormation] || FORMATION_POSITIONS["4-3-3"];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Tactics &amp; Formation
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Current setup · FC Metropolitan
          </SoftTypography>
        </SoftBox>

        {/* Formation selector */}
        <SoftBox display="flex" gap={1.5} mb={3} flexWrap="wrap">
          {FORMATIONS.map((f) => (
            <SoftButton
              key={f}
              variant={activeFormation === f ? "gradient" : "outlined"}
              color={activeFormation === f ? "info" : "secondary"}
              size="small"
              onClick={() => setActiveFormation(f)}
            >
              {f}
            </SoftButton>
          ))}
        </SoftBox>

        <Grid container spacing={3}>
          {/* Pitch */}
          <Grid item xs={12} lg={7}>
            <Card>
              <SoftBox p={2}>
                <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                  {activeFormation} Formation
                </SoftTypography>
                {/* Football pitch */}
                <SoftBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    paddingBottom: "140%",
                    background: "linear-gradient(180deg, #2d8a4e 0%, #267a46 50%, #2d8a4e 100%)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "3px solid #1a6b38",
                  }}
                >
                  {/* Pitch markings */}
                  <SoftBox sx={{ position: "absolute", inset: 0 }}>
                    {/* Centre circle */}
                    <SoftBox sx={{ position: "absolute", top: "47%", left: "50%", transform: "translate(-50%, -50%)", width: "18%", paddingBottom: "18%", border: "2px solid rgba(255,255,255,0.5)", borderRadius: "50%" }} />
                    {/* Centre line */}
                    <SoftBox sx={{ position: "absolute", top: "50%", left: "5%", right: "5%", height: "2px", backgroundColor: "rgba(255,255,255,0.5)" }} />
                    {/* Penalty areas */}
                    <SoftBox sx={{ position: "absolute", top: "5%", left: "20%", right: "20%", height: "15%", border: "2px solid rgba(255,255,255,0.5)" }} />
                    <SoftBox sx={{ position: "absolute", bottom: "5%", left: "20%", right: "20%", height: "15%", border: "2px solid rgba(255,255,255,0.5)" }} />
                    {/* Outer boundary */}
                    <SoftBox sx={{ position: "absolute", top: "3%", left: "3%", right: "3%", bottom: "3%", border: "2px solid rgba(255,255,255,0.7)", borderRadius: "4px" }} />
                  </SoftBox>

                  {/* Player dots */}
                  {positions.map((pos, i) => {
                    const isGK = pos.label === "GK";
                    return (
                      <SoftBox
                        key={pos.label}
                        sx={{
                          position: "absolute",
                          left: `${pos.x}%`,
                          top: `${pos.y}%`,
                          transform: "translate(-50%, -50%)",
                          textAlign: "center",
                          zIndex: 2,
                        }}
                      >
                        <SoftBox
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: isGK
                              ? "linear-gradient(195deg, #FDD835, #F57F17)"
                              : "linear-gradient(195deg, #42424a, #191919)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid white",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                            cursor: "pointer",
                            mx: "auto",
                          }}
                        >
                          <SoftTypography variant="caption" color="white" fontWeight="bold" sx={{ fontSize: "0.6rem" }}>
                            {LINEUP_PLAYERS[i]?.split(" ")[1]?.substring(0, 4) || pos.label}
                          </SoftTypography>
                        </SoftBox>
                        <SoftTypography
                          variant="caption"
                          sx={{ color: "white", fontSize: "0.6rem", display: "block", textShadow: "0 1px 2px rgba(0,0,0,0.8)", mt: 0.3, fontWeight: 600 }}
                        >
                          {pos.label}
                        </SoftTypography>
                      </SoftBox>
                    );
                  })}
                </SoftBox>

                {/* Subs */}
                <SoftBox mt={2}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                    SUBSTITUTES
                  </SoftTypography>
                  <SoftBox display="flex" flexWrap="wrap" gap={1}>
                    {SUBS.map((s) => (
                      <SoftBadge key={s} badgeContent={s} color="secondary" size="xs" container />
                    ))}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>

          {/* Player assignments + notes */}
          <Grid item xs={12} lg={5}>
            <Grid container spacing={2} direction="column">
              <Grid item>
                <Card>
                  <SoftBox p={2}>
                    <SoftTypography variant="h6" fontWeight="medium" mb={1.5}>
                      Lineup
                    </SoftTypography>
                    {LINEUP_PLAYERS.map((name, i) => (
                      <SoftBox key={name} display="flex" justifyContent="space-between" alignItems="center" mb={0.5} py={0.5} sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftTypography variant="caption" color="text">
                          {positions[i]?.label}
                        </SoftTypography>
                        <SoftTypography variant="button" fontWeight="medium">
                          {name}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </Card>
              </Grid>
              <Grid item>
                <Card>
                  <SoftBox p={2}>
                    <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                      Tactical Notes
                    </SoftTypography>
                    <SoftBox
                      component="textarea"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={5}
                      sx={{ width: "100%", resize: "vertical", border: "1px solid #e9ecef", borderRadius: "8px", p: 1.5, fontFamily: "inherit", fontSize: "0.85rem", color: "#344767", "&:focus": { outline: "none", borderColor: "#17c1e8" } }}
                    />
                    <SoftButton variant="gradient" color="info" size="small" sx={{ mt: 1 }}>
                      Save Tactics
                    </SoftButton>
                  </SoftBox>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default Tactics;
